import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { notificationEventsApi, emailTemplatesApi, smsTemplatesApi, pushTemplatesApi } from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import * as LucideIcons from 'lucide-react'
import {
  Mail, Smartphone, Bell, Settings, ChevronDown, ChevronRight, Save,
  Pencil, Trash2, Send, ShieldCheck, UserCircle, UserPlus, Dumbbell,
  Car, Megaphone, Package, Wrench, Building2, Palette, Image, AlertTriangle,
  CheckCircle2, Circle, Zap
} from 'lucide-react'
import {
  PageHeader,
  Button,
  LoadingSpinner,
  ErrorMessage,
  Badge,
  Card,
  CardBody
} from '../components/common'
import BatchRegenerateModal from '../components/BatchRegenerateModal'
import SmsBatchGenerateModal from '../components/SmsBatchGenerateModal'
import PushBatchGenerateModal from '../components/PushBatchGenerateModal'
import SendTestModal from '../components/SendTestModal'
import SendSmsTestModal from '../components/SendSmsTestModal'
import SendPushTestModal from '../components/SendPushTestModal'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getIcon = (name) => {
  if (!name) return Bell
  const pascalName = name
    .split('-')
    .map(s => s.charAt(0).toUpperCase() + s.slice(1))
    .join('')
  return LucideIcons[pascalName] || Bell
}

/** Split PascalCase / camelCase into spaced words. */
const humanize = (str) =>
  str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')

/** Count how many required variables a template body is missing. */
function getVariableCoverage(messageBody, variablesList) {
  if (!messageBody || !variablesList || variablesList.length === 0) return null
  const usedVars = [...messageBody.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1])
  const required = variablesList.filter(v => v.isRequired).map(v => v.name)
  const missingRequired = required.filter(name => !usedVars.includes(name))
  return { usedVars, requiredCount: required.length, missingRequired, allRequiredCovered: missingRequired.length === 0 }
}

// ---------------------------------------------------------------------------
// Category mappings per channel
// ---------------------------------------------------------------------------

// Grouped by module, mirroring PUSH_CATEGORIES below so all three channels read the same.
// Names must match the EmailTemplateType enum on the server — anything unmatched falls into "Other".
const EMAIL_CATEGORIES = {
  'Authentication': ['RegistrationConfirmation', 'EmailVerification', 'ForgotPassword'],
  'Account': ['WelcomeToBuilding', 'EmailChangeNotification'],
  'Buildings': ['RegisterNewBuilding', 'LicenseActivated'],
  'Join Requests': ['RequestToJoinBuilding', 'RequestToJoinBuildingAccepted', 'RequestToJoinBuildingRejected'],
  'Announcements': ['Announcement', 'AnnouncementReminder'],
  'Deliveries': ['DeliveryNotification'],
  'Amenities': ['AmenityBookingConfirmation', 'AmenityBookingDecline', 'AmenityBookingCancellation', 'AdminAmenityBookingRequest'],
  'Parking': ['ParkingBookingConfirmation', 'ParkingBookingDecline', 'ParkingBookingCancellation', 'ParkingCheckInExpired', 'AdminParkingBookingRequest'],
  'Maintenance': ['TaskCreated', 'TaskAssigned', 'TaskEdited', 'TaskCommentAdded'],
  'System': ['TestTemplate']
}

const SMS_CATEGORIES = {
  'Authentication': ['SignUp', 'ForgotPassword', 'ResetPassword'],
  'Welcome & Account': ['WelcomeMessage', 'BuildingInvitation'],
  'Notifications': ['Announcement', 'DeliveryNotification', 'Reminder'],
  'Amenities': ['AmenityBookingConfirmation', 'AmenityBookingCancellation', 'AdminAmenityBookingRequest'],
  'Parking': ['ParkingBookingConfirmation', 'ParkingBookingCancellation', 'ParkingCheckInExpired', 'AdminParkingBookingRequest'],
  'System': ['TestTemplate']
}

const PUSH_CATEGORIES = {
  'Announcements': ['Announcement', 'AnnouncementReminder'],
  'Deliveries': ['DeliveryNotification'],
  'Amenities': ['AmenityBookingConfirmation', 'AmenityBookingDecline', 'AmenityBookingCancellation', 'AdminAmenityBookingRequest'],
  'Parking': ['ParkingBookingConfirmation', 'ParkingBookingDecline', 'ParkingBookingCancellation', 'ParkingCheckInExpired', 'AdminParkingBookingRequest'],
  'Maintenance': ['TaskCreated', 'TaskAssigned', 'TaskEdited', 'TaskCommentAdded']
}

const CATEGORY_ICON_MAP = {
  'Authentication': ShieldCheck,
  'Account': UserCircle,
  'Notifications': Bell,
  'System': Settings,
  'Welcome & Account': UserPlus,
  'Amenities': Dumbbell,
  'Parking': Car,
  'Announcements': Megaphone,
  'Deliveries': Package,
  'Maintenance': Wrench,
  'Buildings': Building2,
  'Join Requests': UserPlus
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const ToggleSwitch = ({ enabled, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={onChange}
    disabled={disabled}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      enabled ? 'bg-primary-500' : 'bg-gray-300'
    } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span
      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
        enabled ? 'translate-x-4' : 'translate-x-0.5'
      }`}
    />
  </button>
)

const TOP_TABS = [
  { id: 'events', label: 'Events', icon: Settings },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'sms', label: 'SMS', icon: Smartphone },
  { id: 'push', label: 'Push', icon: Bell },
]

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const NotificationEvents = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // ---- Shared state ----
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [collapsedCategories, setCollapsedCategories] = useState(new Set())
  const [changedEvents, setChangedEvents] = useState(new Map())
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab')
    return ['events', 'email', 'sms', 'push'].includes(tabParam) ? tabParam : 'events'
  })

  // ---- Modal state ----
  const [showEmailBatchModal, setShowEmailBatchModal] = useState(false)
  const [showSmsBatchModal, setShowSmsBatchModal] = useState(false)
  const [showPushBatchModal, setShowPushBatchModal] = useState(false)

  const [emailTestTemplate, setEmailTestTemplate] = useState(null)
  const [smsTestTemplate, setSmsTestTemplate] = useState(null)
  const [pushTestTemplate, setPushTestTemplate] = useState(null)

  // ---- Template channel data (lazy-loaded) ----
  const [emailData, setEmailData] = useState({ loaded: false, loading: false, types: [], templates: [], variablesMap: {} })
  const [smsData, setSmsData] = useState({ loaded: false, loading: false, types: [], templates: [], variablesMap: {} })
  const [pushData, setPushData] = useState({ loaded: false, loading: false, types: [], templates: [], variablesMap: {} })

  // =========================================================================
  // Load notification events (always loaded on mount)
  // =========================================================================

  useEffect(() => {
    loadEvents()
  }, [])

  const loadEvents = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await notificationEventsApi.list()
      setCategories(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      setError(extractErrorMessage(err))
      setCategories([])
    } finally {
      setLoading(false)
    }
  }

  // =========================================================================
  // Lazy-load template data per channel
  // =========================================================================

  useEffect(() => {
    if (activeTab === 'email' && !emailData.loaded && !emailData.loading) loadEmailData()
    if (activeTab === 'sms' && !smsData.loaded && !smsData.loading) loadSmsData()
    if (activeTab === 'push' && !pushData.loaded && !pushData.loading) loadPushData()
  }, [activeTab]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadEmailData = async () => {
    setEmailData(prev => ({ ...prev, loading: true }))
    try {
      const [typesRes, templatesRes] = await Promise.all([
        emailTemplatesApi.getTypes(),
        emailTemplatesApi.list()
      ])
      const types = Array.isArray(typesRes.data) ? typesRes.data : []
      const templates = Array.isArray(templatesRes.data) ? templatesRes.data : []

      // Load variables for all types in parallel
      const varEntries = await Promise.all(
        types.map(async (t) => {
          try {
            const res = await emailTemplatesApi.getVariablesByType(t.value)
            const vars = res.data?.variables || (Array.isArray(res.data) ? res.data : [])
            return [t.value, vars]
          } catch {
            return [t.value, []]
          }
        })
      )
      const variablesMap = Object.fromEntries(varEntries)
      setEmailData({ loaded: true, loading: false, types, templates, variablesMap })
    } catch (err) {
      setError(extractErrorMessage(err))
      setEmailData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadSmsData = async () => {
    setSmsData(prev => ({ ...prev, loading: true }))
    try {
      const [typesRes, templatesRes] = await Promise.all([
        smsTemplatesApi.getTypes(),
        smsTemplatesApi.list()
      ])
      const types = Array.isArray(typesRes.data) ? typesRes.data : []
      const templates = Array.isArray(templatesRes.data) ? templatesRes.data : []

      const varEntries = await Promise.all(
        types.map(async (t) => {
          try {
            const res = await smsTemplatesApi.getVariablesByType(t.value)
            const vars = res.data?.variables || (Array.isArray(res.data) ? res.data : [])
            return [t.value, vars]
          } catch {
            return [t.value, []]
          }
        })
      )
      const variablesMap = Object.fromEntries(varEntries)
      setSmsData({ loaded: true, loading: false, types, templates, variablesMap })
    } catch (err) {
      setError(extractErrorMessage(err))
      setSmsData(prev => ({ ...prev, loading: false }))
    }
  }

  const loadPushData = async () => {
    setPushData(prev => ({ ...prev, loading: true }))
    try {
      const [typesRes, templatesRes] = await Promise.all([
        pushTemplatesApi.getTypes(),
        pushTemplatesApi.list()
      ])
      const types = Array.isArray(typesRes.data) ? typesRes.data : []
      const templates = Array.isArray(templatesRes.data) ? templatesRes.data : []

      const varEntries = await Promise.all(
        types.map(async (t) => {
          try {
            const res = await pushTemplatesApi.getVariablesByType(t.value)
            const vars = res.data?.variables || (Array.isArray(res.data) ? res.data : [])
            return [t.value, vars]
          } catch {
            return [t.value, []]
          }
        })
      )
      const variablesMap = Object.fromEntries(varEntries)
      setPushData({ loaded: true, loading: false, types, templates, variablesMap })
    } catch (err) {
      setError(extractErrorMessage(err))
      setPushData(prev => ({ ...prev, loading: false }))
    }
  }

  // =========================================================================
  // Events tab — channel toggle logic (unchanged from original)
  // =========================================================================

  const toggleCategory = useCallback((categoryName) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev)
      if (next.has(categoryName)) {
        next.delete(categoryName)
      } else {
        next.add(categoryName)
      }
      return next
    })
  }, [])

  const handleToggle = useCallback((eventType, channel, currentEnabled) => {
    setChangedEvents(prev => {
      const next = new Map(prev)
      const existing = next.get(eventType)

      let original = null
      for (const cat of categories) {
        const evt = cat.events?.find(e => e.eventType === eventType)
        if (evt) {
          original = evt
          break
        }
      }
      if (!original) return prev

      const current = existing || {
        emailEnabled: original.emailEnabled,
        smsEnabled: original.smsEnabled,
        pushEnabled: original.pushEnabled,
      }

      const updated = {
        ...current,
        [`${channel}Enabled`]: !currentEnabled,
      }

      if (
        updated.emailEnabled === original.emailEnabled &&
        updated.smsEnabled === original.smsEnabled &&
        updated.pushEnabled === original.pushEnabled
      ) {
        next.delete(eventType)
      } else {
        next.set(eventType, updated)
      }

      return next
    })
  }, [categories])

  const getEffectiveValue = useCallback((event, channel) => {
    const changed = changedEvents.get(event.eventType)
    if (changed) {
      return changed[`${channel}Enabled`]
    }
    return event[`${channel}Enabled`]
  }, [changedEvents])

  const handleSave = async () => {
    if (changedEvents.size === 0 || saving) return
    try {
      setSaving(true)
      const promises = []
      for (const [eventType, data] of changedEvents) {
        promises.push(notificationEventsApi.updateChannels(eventType, data))
      }
      await Promise.all(promises)
      setChangedEvents(new Map())
      await loadEvents()
    } catch (err) {
      showError(err)
    } finally {
      setSaving(false)
    }
  }

  // =========================================================================
  // Template tab — delete handler
  // =========================================================================

  const handleDeleteTemplate = async (channel, templateType) => {
    const channelLabel = channel === 'email' ? 'email' : channel === 'sms' ? 'SMS' : 'push'
    if (!window.confirm(`Delete this ${channelLabel} template? This cannot be undone.`)) return

    try {
      if (channel === 'email') {
        await emailTemplatesApi.delete(templateType)
        setEmailData(prev => ({
          ...prev,
          templates: prev.templates.filter(t => parseInt(t.templateType) !== templateType)
        }))
      } else if (channel === 'sms') {
        await smsTemplatesApi.delete(templateType)
        setSmsData(prev => ({
          ...prev,
          templates: prev.templates.filter(t => parseInt(t.templateType) !== templateType)
        }))
      } else {
        await pushTemplatesApi.delete(templateType)
        setPushData(prev => ({
          ...prev,
          templates: prev.templates.filter(t => parseInt(t.templateType) !== templateType)
        }))
      }
    } catch (err) {
      showError(err)
    }
  }

  // =========================================================================
  // Template tab — unified row renderer
  // =========================================================================

  /**
   * Render a template tab (email / sms / push) with collapsible category rows.
   *
   * @param {'email'|'sms'|'push'} channel
   * @param {object} data            - { types, templates, variablesMap }
   * @param {object} categoryMap     - e.g. EMAIL_CATEGORIES
   */
  const renderTemplateTab = (channel, data, categoryMap) => {
    const { types, templates, variablesMap } = data

    // Build a lookup: typeName -> type object  &  typeValue -> template object
    const typeByName = {}
    const typeByValue = {}
    types.forEach(t => {
      typeByName[t.name] = t
      typeByValue[t.value] = t
    })

    const templateByValue = {}
    templates.forEach(t => {
      templateByValue[parseInt(t.templateType)] = t
    })

    // Build ordered categories. Any template type not in a category goes into "Other".
    const categorized = new Set()
    const orderedCategories = Object.entries(categoryMap).map(([catName, typeNames]) => {
      const items = typeNames
        .map(name => {
          const type = typeByName[name]
          if (!type) return null
          categorized.add(name)
          const template = templateByValue[type.value]
          const vars = variablesMap[type.value] || []
          return { type, template, vars }
        })
        .filter(Boolean)
      return { name: catName, items }
    }).filter(c => c.items.length > 0)

    // Collect uncategorized types into an "Other" bucket
    const uncategorized = types
      .filter(t => !categorized.has(t.name))
      .map(t => ({
        type: t,
        template: templateByValue[t.value],
        vars: variablesMap[t.value] || []
      }))
    if (uncategorized.length > 0) {
      orderedCategories.push({ name: 'Other', items: uncategorized })
    }

    // Navigation helpers per channel
    const getEditUrl = (typeValue) => {
      if (channel === 'email') return `/email-templates/edit/${typeValue}`
      if (channel === 'sms') return `/sms-templates/edit/${typeValue}`
      return `/notifications/push/${typeValue}/edit`
    }

    const getCreateUrl = (typeValue) => {
      if (channel === 'email') return `/email-templates/new?type=${typeValue}`
      if (channel === 'sms') return `/sms-templates/new?type=${typeValue}`
      return `/notifications/push/${typeValue}/edit`
    }

    // Column headers
    const columnHeaders = (
      <div className="flex items-center px-6 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
        <div className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Template
        </div>
        <div className="w-28 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Status
        </div>
        <div className="w-40 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Variables
        </div>
        <div className="w-32 text-right pr-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          Actions
        </div>
      </div>
    )

    return (
      <Card>
        <CardBody className="p-0">
          {columnHeaders}

          {orderedCategories.map((cat) => {
            const CatIcon = CATEGORY_ICON_MAP[cat.name] || Bell
            const catKey = `${channel}-${cat.name}`
            const isCollapsed = collapsedCategories.has(catKey)

            return (
              <div key={catKey}>
                {/* Category header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(catKey)}
                  className="w-full flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 border-b border-gray-100 transition-colors"
                >
                  {isCollapsed ? (
                    <ChevronRight size={16} className="text-gray-400" />
                  ) : (
                    <ChevronDown size={16} className="text-gray-400" />
                  )}
                  <CatIcon size={18} className="text-primary-500" />
                  <span className="text-sm font-semibold text-gray-900">
                    {cat.name}
                  </span>
                  <Badge variant="default" size="small">
                    {cat.items.length}
                  </Badge>
                </button>

                {/* Template rows */}
                {!isCollapsed && cat.items.map(({ type, template, vars }) => {
                  const isConfigured = !!template
                  const requiredVars = vars.filter(v => v.isRequired)
                  const optionalVars = vars.filter(v => !v.isRequired)

                  // Variable coverage for channels that use {{var}} in body
                  let coverage = null
                  if (isConfigured && channel === 'sms') {
                    const body = template.messageBody || ''
                    coverage = getVariableCoverage(body, vars)
                  }
                  if (isConfigured && channel === 'push') {
                    const combined = (template.title || '') + ' ' + (template.messageBody || '')
                    coverage = getVariableCoverage(combined, vars)
                  }
                  if (isConfigured && channel === 'email') {
                    const body = (template.htmlBody || '') + ' ' + (template.textBody || '')
                    coverage = getVariableCoverage(body, vars)
                  }

                  return (
                    <div
                      key={type.value}
                      className="flex items-center pl-12 pr-6 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors group cursor-pointer"
                      onClick={() => navigate(isConfigured ? getEditUrl(type.value) : getCreateUrl(type.value))}
                    >
                      {/* Template name */}
                      <div className="flex-1 flex items-center gap-3 min-w-0">
                        <div className="p-1.5 rounded-md bg-gray-100">
                          <CatIcon size={14} className="text-gray-600" />
                        </div>
                        <span className="text-sm text-gray-700 truncate">
                          {humanize(type.name)}
                        </span>
                      </div>

                      {/* Status badge */}
                      <div className="w-28 flex justify-center">
                        {isConfigured ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700">
                            <CheckCircle2 size={12} />
                            Configured
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                            <Circle size={12} />
                            Not Set
                          </span>
                        )}
                      </div>

                      {/* Variables */}
                      <div className="w-40 flex justify-center gap-1.5 flex-wrap">
                        {vars.length === 0 ? (
                          <span className="text-xs text-gray-400">--</span>
                        ) : (
                          <>
                            {requiredVars.length > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-50 text-red-600">
                                {requiredVars.length} req
                              </span>
                            )}
                            {optionalVars.length > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600">
                                {optionalVars.length} opt
                              </span>
                            )}
                            {isConfigured && coverage && !coverage.allRequiredCovered && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-yellow-50 text-yellow-700"
                                title={`Missing: ${coverage.missingRequired.join(', ')}`}
                              >
                                <AlertTriangle size={10} className="mr-0.5" />
                                {coverage.missingRequired.length} missing
                              </span>
                            )}
                          </>
                        )}
                      </div>

                      {/* Actions */}
                      <div
                        className="w-32 flex justify-end gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isConfigured ? (
                          <>
                            <button
                              type="button"
                              title="Edit"
                              onClick={() => navigate(getEditUrl(type.value))}
                              className="p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                            >
                              <Pencil size={14} />
                            </button>
                            {channel === 'email' && (
                              <button
                                type="button"
                                title="Send Test"
                                onClick={() => setEmailTestTemplate({ template, typeName: humanize(type.name) })}
                                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              >
                                <Send size={14} />
                              </button>
                            )}
                            {channel === 'sms' && (
                              <button
                                type="button"
                                title="Send Test"
                                onClick={() => setSmsTestTemplate({ template, typeName: humanize(type.name) })}
                                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              >
                                <Send size={14} />
                              </button>
                            )}
                            {channel === 'push' && (
                              <button
                                type="button"
                                title="Send Test"
                                onClick={() => setPushTestTemplate({ template, typeName: humanize(type.name) })}
                                className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                              >
                                <Send size={14} />
                              </button>
                            )}
                            <button
                              type="button"
                              title="Delete"
                              onClick={() => handleDeleteTemplate(channel, type.value)}
                              className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            title="Create"
                            onClick={() => navigate(getCreateUrl(type.value))}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                          >
                            <Zap size={12} />
                            Create
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )
          })}

          {orderedCategories.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-gray-500">
              No template types available for this channel.
            </div>
          )}
        </CardBody>
      </Card>
    )
  }

  // =========================================================================
  // Header action per tab
  // =========================================================================

  const changeCount = changedEvents.size

  const getHeaderAction = () => {
    if (activeTab === 'events' && changeCount > 0) {
      return {
        label: saving ? 'Saving...' : `Save Changes (${changeCount})`,
        onClick: handleSave,
        icon: Save,
        disabled: saving,
      }
    }
    if (activeTab === 'email') {
      return {
        label: 'Batch Generate',
        onClick: () => setShowEmailBatchModal(true),
      }
    }
    if (activeTab === 'sms') {
      return {
        label: 'Batch Generate',
        onClick: () => setShowSmsBatchModal(true),
      }
    }
    if (activeTab === 'push') {
      return {
        label: 'Batch Generate',
        onClick: () => setShowPushBatchModal(true),
      }
    }
    return undefined
  }

  // =========================================================================
  // Render
  // =========================================================================

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        subtitle="Manage notification channels, templates, and delivery preferences"
        action={getHeaderAction()}
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Top-level tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {TOP_TABS.map((tab) => {
            const TabIcon = tab.icon
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setActiveTab(tab.id)
                  navigate(tab.id === 'events' ? '/notifications' : `/notifications?tab=${tab.id}`, { replace: true })
                }}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <TabIcon size={16} />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* ================================================================= */}
      {/* Tab 1: Events — channel configuration toggles                     */}
      {/* ================================================================= */}
      {activeTab === 'events' && (
        <Card>
          <CardBody className="p-0">
            {/* Column Headers */}
            <div className="flex items-center px-6 py-3 bg-gray-50 border-b border-gray-200 rounded-t-lg">
              <div className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Event
              </div>
              <div className="w-20 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Email
              </div>
              <div className="w-20 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                SMS
              </div>
              <div className="w-20 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Push
              </div>
            </div>

            {/* Category Sections */}
            {categories.map((category) => {
              const CategoryIcon = getIcon(category.icon)
              const isCollapsed = collapsedCategories.has(category.category)

              return (
                <div key={category.category}>
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(category.category)}
                    className="w-full flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 border-b border-gray-100 transition-colors"
                  >
                    {isCollapsed ? (
                      <ChevronRight size={16} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={16} className="text-gray-400" />
                    )}
                    <CategoryIcon size={18} className="text-primary-500" />
                    <span className="text-sm font-semibold text-gray-900">
                      {category.category}
                    </span>
                    <Badge variant="default" size="small">
                      {category.events?.length || 0}
                    </Badge>
                  </button>

                  {/* Event Rows */}
                  {!isCollapsed && category.events?.map((event) => {
                    const EventIcon = getIcon(event.icon)
                    const emailEnabled = getEffectiveValue(event, 'email')
                    const smsEnabled = getEffectiveValue(event, 'sms')
                    const pushEnabled = getEffectiveValue(event, 'push')
                    const isChanged = changedEvents.has(event.eventType)

                    return (
                      <div
                        key={event.eventType}
                        className={`flex items-center pl-12 pr-6 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                          isChanged ? 'bg-primary-50/50' : ''
                        }`}
                      >
                        <div className="flex-1 flex items-center gap-3">
                          <div className="p-1.5 rounded-md bg-gray-100">
                            <EventIcon size={14} className="text-gray-600" />
                          </div>
                          <span className="text-sm text-gray-700">
                            {event.displayName}
                          </span>
                          {isChanged && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded bg-primary-100 text-primary-700">
                              Modified
                            </span>
                          )}
                        </div>
                        <div className="w-20 flex justify-center">
                          <ToggleSwitch
                            enabled={emailEnabled}
                            onChange={() => handleToggle(event.eventType, 'email', emailEnabled)}
                            disabled={!event.emailConfigured && !emailEnabled}
                          />
                        </div>
                        <div className="w-20 flex justify-center">
                          <ToggleSwitch
                            enabled={smsEnabled}
                            onChange={() => handleToggle(event.eventType, 'sms', smsEnabled)}
                            disabled={!event.smsConfigured && !smsEnabled}
                          />
                        </div>
                        <div className="w-20 flex justify-center">
                          <ToggleSwitch
                            enabled={pushEnabled}
                            onChange={() => handleToggle(event.eventType, 'push', pushEnabled)}
                            disabled={!event.pushConfigured && !pushEnabled}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </CardBody>
        </Card>
      )}

      {/* ================================================================= */}
      {/* Tab 2: Email Templates                                            */}
      {/* ================================================================= */}
      {activeTab === 'email' && (
        <>
          {/* Quick links to branding / assets */}
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="small"
              onClick={() => navigate('/email-templates?tab=branding')}
              icon={<Palette />}
            >
              Branding Settings
            </Button>
            <Button
              variant="ghost"
              size="small"
              onClick={() => navigate('/email-templates?tab=assets')}
              icon={<Image />}
            >
              Manage Assets
            </Button>
          </div>

          {emailData.loading ? (
            <LoadingSpinner />
          ) : (
            renderTemplateTab('email', emailData, EMAIL_CATEGORIES)
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Tab 3: SMS Templates                                              */}
      {/* ================================================================= */}
      {activeTab === 'sms' && (
        <>
          {smsData.loading ? (
            <LoadingSpinner />
          ) : (
            renderTemplateTab('sms', smsData, SMS_CATEGORIES)
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Tab 4: Push Templates                                             */}
      {/* ================================================================= */}
      {activeTab === 'push' && (
        <>
          {pushData.loading ? (
            <LoadingSpinner />
          ) : (
            renderTemplateTab('push', pushData, PUSH_CATEGORIES)
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Modals                                                            */}
      {/* ================================================================= */}

      {/* Email batch generate */}
      {showEmailBatchModal && emailData.loaded && (
        <BatchRegenerateModal
          templateTypes={emailData.types}
          existingTemplates={emailData.templates}
          onClose={() => setShowEmailBatchModal(false)}
          onComplete={() => loadEmailData()}
        />
      )}

      {/* SMS batch generate */}
      {showSmsBatchModal && smsData.loaded && (
        <SmsBatchGenerateModal
          templateTypes={smsData.types}
          existingTemplates={smsData.templates}
          variablesMap={smsData.variablesMap}
          onClose={() => setShowSmsBatchModal(false)}
          onComplete={() => loadSmsData()}
        />
      )}

      {/* Push batch generate */}
      {showPushBatchModal && pushData.loaded && (
        <PushBatchGenerateModal
          templateTypes={pushData.types}
          existingTemplates={pushData.templates}
          variablesMap={pushData.variablesMap}
          onClose={() => setShowPushBatchModal(false)}
          onComplete={() => loadPushData()}
        />
      )}

      {/* Email send test */}
      {emailTestTemplate && (
        <SendTestModal
          template={emailTestTemplate.template}
          templateTypeName={emailTestTemplate.typeName}
          onCancel={() => setEmailTestTemplate(null)}
        />
      )}

      {/* SMS send test */}
      {smsTestTemplate && (
        <SendSmsTestModal
          template={smsTestTemplate.template}
          templateTypeName={smsTestTemplate.typeName}
          onCancel={() => setSmsTestTemplate(null)}
        />
      )}

      {/* Push send test */}
      {pushTestTemplate && (
        <SendPushTestModal
          template={pushTestTemplate.template}
          templateTypeName={pushTestTemplate.typeName}
          onCancel={() => setPushTestTemplate(null)}
        />
      )}
    </div>
  )
}

export default NotificationEvents
